/**
 * Formatting utilities for the Aurumrun application
 */
import { MathUtil } from './math'
import { translate, activeLocale } from '../i18n/t'

// Fallback price - use useXautPrice() composable for real-time price
export const XAUT_PRICE_USD_FALLBACK = 2650

/**
 * Format XAUT (gold token) amount with appropriate decimal places.
 * When decimals is provided, formats to exactly that many decimal places.
 * Otherwise preserves full precision from backend, stripping trailing zeros (min 2 decimals).
 * @param value - The amount to format (string or number)
 * @param decimals - Optional fixed decimal places
 * @returns Formatted string
 */
export function formatXAUT(value: string | number, decimals?: number): string {
  const trimmed = typeof value === 'string' ? value.trim() : value

  // Fixed decimal mode: format to exact number of decimals
  if (decimals !== undefined) {
    const num = typeof trimmed === 'string' ? parseFloat(trimmed) : trimmed
    if (isNaN(num)) return '0'
    return num.toFixed(decimals)
  }

  // If backend sends a string with decimals, preserve full precision — just strip trailing zeros
  if (typeof trimmed === 'string' && trimmed.includes('.')) {
    const num = parseFloat(trimmed)
    if (isNaN(num)) return '0'
    const stripped = trimmed.replace(/0+$/, '').replace(/\.$/, '')
    const dotIndex = stripped.indexOf('.')
    const currentDecimals = dotIndex === -1 ? 0 : stripped.length - dotIndex - 1
    if (currentDecimals < 2) {
      return num.toFixed(2)
    }
    return stripped
  }

  const num = typeof trimmed === 'string' ? parseFloat(trimmed) : trimmed
  if (isNaN(num)) return '0'
  // For numbers: show all significant decimals, min 2
  const str = num.toString()
  const dotIndex = str.indexOf('.')
  const currentDecimals = dotIndex === -1 ? 0 : str.length - dotIndex - 1
  if (currentDecimals < 2) return num.toFixed(2)
  return str
}

/**
 * Format an XAUT balance for display: up to 8 decimals, trailing zeros stripped
 * (min 2 kept), never scientific notation, so tiny sub-precision balances
 * (e.g. 9.5e-7 -> "0.00000095") stay visible. A negative balance renders with
 * its sign ("-5.00") rather than being masked to "0.00" -- a chargeback clawback
 * can push a withdrawable balance into an outstanding debt that must be shown
 * honestly. Only a true zero or a non-finite input collapses to "0.00".
 * @param value - The XAUT amount
 * @returns Formatted string
 */
export function formatXautPrecise(value: number): string {
  if (!Number.isFinite(value) || value === 0) return '0.00'
  return value.toFixed(8).replace(/(\.\d{2}\d*?)0+$/, '$1')
}

/**
 * Format an already-converted USD amount as a currency string.
 *
 * Non-zero magnitudes below one cent render as `<$0.01` (`-<$0.01` when
 * negative) instead of misleadingly collapsing to `$0.00` — so a tiny but
 * real amount reads differently from a true zero. A genuine `0` (and `NaN`)
 * still renders as `$0.00` (as do non-finite inputs — `NaN`/`±Infinity`).
 * Behaves like the admin panel's `formatUsd`.
 * @param usd - The USD amount (already converted from XAUT)
 * @returns Formatted USD string
 */
export function formatUsdAmount(usd: number): string {
  if (!Number.isFinite(usd) || usd === 0) return '$0.00'
  const abs = Math.abs(usd)
  const sign = usd < 0 ? '-' : ''
  if (abs < 0.01) return `${sign}<$0.01`
  return `${sign}${new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(abs)}`
}

/**
 * Format a configured USD *limit* — a minimum deposit, a daily allowance.
 *
 * Distinct from `formatUsdAmount`, which always shows cents because it renders
 * measured money (a balance, a fee) where "$10" and "$10.00" mean different
 * things. Limits are set by hand in whole dollars, so the cents are noise:
 * this renders "$10", "$50,000" — and still "$12.50" when someone configures a
 * fractional limit. Keeps grouping so a raw `${{ n }}` never prints "$50000".
 *
 * @param usd - The USD limit
 * @returns Formatted USD string with cents only when they carry meaning
 */
export function formatUsdLimit(usd: number): string {
  if (!Number.isFinite(usd)) return '$0'
  const abs = Math.abs(usd)
  const sign = usd < 0 ? '-' : ''
  const hasCents = Math.round(abs * 100) % 100 !== 0
  return `${sign}${new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2
  }).format(abs)}`
}

/**
 * Format a non-USD ("local currency") amount to exactly two decimals — the
 * companion to `formatUsdAmount` for limits the server states in USD but the
 * user enters in their own currency (a $10 minimum shown as "200.00 MXN").
 *
 * `round` keeps a converted limit honest once it is cut to cents: 'ceil' for a
 * minimum (the displayed figure must never sit below the real floor, or paying
 * it exactly gets rejected) and 'floor' for a remaining allowance (never
 * display more than is actually left). Grouping is en-US to match
 * `formatUsdAmount`, so both halves of a "$10 (200.00 MXN)" pair read alike.
 * No currency symbol — the caller supplies the code.
 * @param value - The amount in the local currency
 * @param round - Cent-rounding direction (default: 'nearest')
 * @returns Formatted amount string
 */
export function formatLocalAmount(
  value: number,
  round: 'ceil' | 'floor' | 'nearest' = 'nearest',
): string {
  if (!Number.isFinite(value)) return '0.00'
  // Normalize before rounding: `value * 100` is not exact in binary floating
  // point, so an amount that is already a whole number of cents can land a
  // hair either side of it (0.07*100 = 7.000000000000001, 4.35*100 =
  // 434.99999999999994) and a bare ceil/floor would then move it a full cent.
  const cents = Number((value * 100).toFixed(6))
  const rounded = round === 'ceil'
    ? Math.ceil(cents)
    : round === 'floor'
      ? Math.floor(cents)
      : Math.round(cents)
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rounded / 100)
}

/**
 * Round each part to whole cents so the parts sum exactly to the cent-rounded
 * total of the parts (largest-remainder method: the leftover cent goes to the
 * parts with the largest fractional cents). Use for itemized money breakdowns
 * that must add up on screen once each line is rounded independently. Assumes
 * non-negative inputs (fees); values are expected to be at least a cent so the
 * `formatUsdAmount` sub-cent convention never applies.
 * @param parts - The part amounts in USD
 * @returns The reconciled parts (same order) and their rounded total
 */
export function reconcileUsdParts(parts: readonly number[]): { parts: number[]; total: number } {
  const cents = parts.map((p) => Math.max(0, p) * 100)
  const totalCents = Math.round(cents.reduce((sum, c) => sum + c, 0))
  const floorCents = cents.map((c) => Math.floor(c))
  const leftover = totalCents - floorCents.reduce((sum, c) => sum + c, 0)
  // Hand each leftover cent to the parts with the largest fractional remainder.
  const getsExtraCent = new Set(
    cents
      .map((c, index) => ({ index, frac: c - Math.floor(c) }))
      .sort((a, b) => b.frac - a.frac)
      .slice(0, leftover)
      .map((entry) => entry.index),
  )
  return {
    parts: floorCents.map((c, index) => (getsExtraCent.has(index) ? c + 1 : c) / 100),
    total: totalCents / 100,
  }
}

/**
 * Convert XAUT to USD and format (uses fallback price)
 * For real-time price, use useXautPrice().formatAsUSD() composable
 * @param xaut - The XAUT amount
 * @param priceUSD - Optional custom price (defaults to fallback)
 * @returns Formatted USD string
 */
export function formatUSD(xaut: string | number, priceUSD: number = XAUT_PRICE_USD_FALLBACK): string {
  const num = typeof xaut === 'string' ? parseFloat(xaut) : xaut
  if (isNaN(num)) return '$0.00'

  const usd = parseFloat(MathUtil.multiply(num, priceUSD))

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(usd)
}

/**
 * Format payout in USD, derived from bet USD × multiplier.
 * This avoids rounding mismatch where bet=$1.00 at 1.18x shows $1.19
 * because XAUT→USD conversion rounds bet and payout independently.
 * Instead: Win = round(betXAUT × price, 2) × multiplier
 */
/** @deprecated Not used anywhere. Use useXautPrice().formatAsUSDT() instead. */
export function formatPayoutUSD(
  betXaut: string | number,
  multiplier: number,
  priceUSD: number = XAUT_PRICE_USD_FALLBACK
): string {
  const num = typeof betXaut === 'string' ? parseFloat(betXaut) : betXaut
  if (isNaN(num) || isNaN(multiplier)) return '$0.00'

  const betUSD = parseFloat(MathUtil.multiply(num, priceUSD))
  const betUSDRounded = Math.round(betUSD * 100) / 100
  const winUSD = betUSDRounded * multiplier

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(winUSD)
}

/**
 * @deprecated Not used anywhere. Use useXautPrice().toUSDT() instead.
 * Convert XAUT to USD and return the numeric value (rounded to cents).
 */
export function xautToUSD(xaut: string | number, priceUSD: number = XAUT_PRICE_USD_FALLBACK): number {
  const num = typeof xaut === 'string' ? parseFloat(xaut) : xaut
  if (isNaN(num)) return 0
  const usd = parseFloat(MathUtil.multiply(num, priceUSD))
  return Math.round(usd * 100) / 100
}

/**
 * Format a multiplier value (e.g., 1.5x)
 * @param value - The multiplier value
 * @returns Formatted multiplier string
 */
export function formatMultiplier(value: number): string {
  if (isNaN(value)) return '0.00x'
  const truncated = MathUtil.truncateMultiplier(value)
  return `${truncated.toFixed(2)}x`
}

/**
 * Format a decimal as a percentage
 * @param value - The decimal value (e.g., 0.15 for 15%)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string
 */
export function formatPercent(value: number, decimals: number = 2): string {
  if (isNaN(value)) return '0%'
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * Format a number in compact notation (e.g., 1.5K, 2.3M)
 * @param value - The number to format
 * @returns Compact formatted string
 */
export function formatCompact(value: number): string {
  if (isNaN(value)) return '0'

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value)
}

/**
 * Format a date as a relative time string (e.g., "5m ago", "2h ago")
 * @param date - The date to format
 * @returns Relative time string
 */
export function formatTimeAgo(date: Date | string): string {
  const now = new Date()
  const then = typeof date === 'string' ? new Date(date) : date

  if (isNaN(then.getTime())) return translate('common.time.unknown')

  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) return translate('common.time.justNow')
  if (seconds < 3600) return translate('common.time.minutesAgo', { n: Math.floor(seconds / 60) })
  if (seconds < 86400) return translate('common.time.hoursAgo', { n: Math.floor(seconds / 3600) })
  if (seconds < 604800) return translate('common.time.daysAgo', { n: Math.floor(seconds / 86400) })

  return then.toLocaleDateString(activeLocale(), {
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Truncate a wallet address for display
 * @param address - The full wallet address
 * @param chars - Number of characters to show on each end (default: 4)
 * @returns Truncated address string
 */
export function truncateAddress(address: string, chars: number = 4): string {
  if (!address) return ''
  if (address.length <= chars * 2 + 3) return address
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

/**
 * Format a number with thousand separators
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export function formatNumber(value: number, decimals: number = 2): string {
  if (isNaN(value)) return '0'

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  }).format(value)
}

/**
 * Format a date in a standard format
 * @param date - The date to format
 * @returns Formatted date string
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return translate('common.time.invalidDate')

  // Locale drives both month-name translation and the 12h/24h clock
  // (en → "Jun 1, 2026, 07:04 AM"; uk → "1 черв. 2026 р., 07:04").
  return d.toLocaleDateString(activeLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Format duration in seconds to a readable format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) return '0s'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}
